// MemberRecord.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { BoxArrowUpRight, ChevronLeft, ChevronRight, Search } from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import useApi2Resource from '../../hooks/useApi2Resource';
import FeedItem from '../feed/FeedItem';
import { Bars, FeedButton, FeedCard, FeedSection, FeedSegments, PanelState } from '../feed/FeedCard';
import TradeRow from '../spending/TradeRow';
import { onActivate, openExternal } from '../../utils/external';
import {
  billDetailPath,
  billStageLabel,
  companyRecordLabel,
  fetchMemberBills,
  fetchMemberCompanies,
  fetchMemberTrades,
  fetchMemberVotes,
  orgIdFromUrl,
  positionLabel,
  voteDetailPath,
  voteKindLabel,
} from '../../utils/officials';
import {
  companyPath,
  formatCount,
  formatDay,
  formatDollars,
  formatPercent,
  titleCaseName,
} from '../../utils/spending';

import '../spending/Spending.css';
import '../elections/parties/Parties.css';
import './MemberRecord.css';

/**
 * MemberRecord
 *
 * Everything api2's /officials/{bioguide}/record holds about a sitting member
 * of Congress, as the panels behind the member page's tabs:
 *
 *   MemberRecordSummary  the headline counts, as lines in the profile
 *   MemberVotesList      roll calls (api2), short or full with search
 *   MemberBillsList      sponsored or cosponsored bills (api2)
 *   MemberMoneyPanel     campaign money and leadership PACs (the Campaign $ tab)
 *   MemberTradesPanel    STOCK Act trades, short (the tab) or full (the modal)
 *   MemberCompaniesPanel companies an official record ties to them
 *   MemberCommitteesPanel committee seats
 *   MemberFilingsPanel   financial disclosure reports
 *
 * Votes, bills and trades are FeedItems, like every other list in the app.
 * The rest are feed cards.
 *
 * ─── What the page must never say ─────────────────────────────────────────────
 *
 * The record's own rules, applied where each number appears rather than in a
 * footnote: nothing is added to anything else; a missed vote is a fact, not a
 * reason; voting with the party is a comparison, not a judgement; a company's
 * tie shows a record exists, not an exchange; trades are ranges; annual
 * reports are linked, not read.
 *
 * ─── Shapes ───────────────────────────────────────────────────────────────────
 *
 * The bills, votes and trades lists follow the documented responses exactly.
 * Inside /record, some sections are documented by what they hold rather than
 * field by field (committees, the newest trades and reports, linked
 * companies), so those are read defensively: each renders from what is there
 * and leaves out what is not.
 */

const isNum = (value) => typeof value === 'number' && !Number.isNaN(value);

// Share hands out the Gov Glance page when an item has one, the same page More
// opens, and the outside record only when it does not.
const SHARE_BASE = 'https://news.govglance.org';
const shareLinkFor = (appPath, outsideUrl) => (appPath ? `${SHARE_BASE}${appPath}` : outsideUrl);

// The first array in an object whose rows look like `probe` says they should.
const findRows = (obj, keys, probe) => {
  if (!obj || typeof obj !== 'object') return [];
  for (const key of keys) {
    if (Array.isArray(obj[key])) return obj[key];
  }
  const found = Object.values(obj).find(
    (value) => Array.isArray(value) && value.length && value.every((row) => row && typeof row === 'object' && probe(row))
  );
  return found || [];
};

const firstOf = (row, keys) =>
  keys.map((key) => row?.[key]).find((value) => value !== undefined && value !== null && value !== '');

const useDebounced = (value, ms = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
};

// Paged api2 list: the first page on any filter change, more on demand.
const usePagedList = (load, deps, pageSize) => {
  const [state, setState] = useState({ rows: [], total: 0, body: null, loading: true, error: null, more: false });

  useEffect(() => {
    let live = true;
    setState({ rows: [], total: 0, body: null, loading: true, error: null, more: false });
    load(0, pageSize)
      .then((body) => {
        if (!live) return;
        setState({
          rows: Array.isArray(body?.results) ? body.results : [],
          total: body?.total ?? 0,
          body,
          loading: false,
          error: null,
          more: false,
        });
      })
      .catch((error) => {
        if (live) setState((current) => ({ ...current, loading: false, error }));
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const loadMore = () => {
    setState((current) => ({ ...current, more: true }));
    load(state.rows.length, pageSize)
      .then((body) => {
        setState((current) => ({
          ...current,
          rows: current.rows.concat(Array.isArray(body?.results) ? body.results : []),
          more: false,
        }));
      })
      .catch(() => setState((current) => ({ ...current, more: false })));
  };

  return { ...state, loadMore, hasMore: state.rows.length < state.total };
};

const SearchBox = ({ value, onChange, placeholder }) => (
  <label className="company-search record-search">
    <Search size={14} aria-hidden />
    <input
      type="search"
      value={value}
      placeholder={placeholder}
      aria-label={placeholder}
      onChange={(event) => onChange(event.target.value)}
    />
  </label>
);

const MoreButton = ({ onClick, loading, label }) => (
  <div className="feed-item record-more">
    <FeedButton primary onClick={onClick} disabled={loading}>
      {loading && <Spinner animation="border" size="sm" className="me-2" />}
      {label}
    </FeedButton>
  </div>
);

// ─── Tab strip ────────────────────────────────────────────────────────────────

/**
 * ScrollableTabStrip
 *
 * The member page's tabs outgrow a phone, and a sideways-scrolling strip with
 * no scrollbar gives no sign there is more. Arrows appear on whichever side has
 * tabs out of view, each over a fade so the strip visibly continues, and
 * pressing one scrolls about two-thirds of the strip's width. At each end the
 * arrow goes away; when every tab fits there are none.
 *
 * When the active tab changes -- a record line in the profile can open Stock
 * trades, far off to the right -- the strip scrolls it into view. It moves
 * only the strip: scrollIntoView would also scroll the page.
 */
export function ScrollableTabStrip({ id, className = '', ariaLabel, activeKey, children }) {
  const { t } = useTranslation('common');
  const stripRef = useRef(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const update = useCallback(() => {
    const strip = stripRef.current;
    if (!strip) return;
    const max = strip.scrollWidth - strip.clientWidth;
    const left = strip.scrollLeft > 2;
    const right = strip.scrollLeft < max - 2;
    setEdges((current) => (current.left === left && current.right === right ? current : { left, right }));
  }, []);

  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return undefined;
    update();
    strip.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (observer) observer.observe(strip);
    return () => {
      strip.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      if (observer) observer.disconnect();
    };
  }, [update]);

  // Tabs can be added after the first paint (the record's tabs when it loads).
  useEffect(() => {
    update();
  });

  useEffect(() => {
    const strip = stripRef.current;
    const tab = strip?.querySelector('[aria-selected="true"]');
    if (!strip || !tab) return;
    const tabLeft = tab.offsetLeft - strip.offsetLeft;
    const tabRight = tabLeft + tab.offsetWidth;
    const pad = 40; // clear of the arrow
    if (tabLeft < strip.scrollLeft + pad) {
      strip.scrollTo({ left: Math.max(0, tabLeft - pad), behavior: 'smooth' });
    } else if (tabRight > strip.scrollLeft + strip.clientWidth - pad) {
      strip.scrollTo({ left: tabRight - strip.clientWidth + pad, behavior: 'smooth' });
    }
  }, [activeKey]);

  const scrollByPage = (direction) => {
    const strip = stripRef.current;
    if (!strip) return;
    strip.scrollBy({ left: direction * strip.clientWidth * 0.66, behavior: 'smooth' });
  };

  return (
    <div className="tab-scroller" id={id}>
      {edges.left && (
        <button
          type="button"
          className="tab-scroller-arrow tab-scroller-arrow--left"
          aria-label={t('common:scrollTabsLeft', 'Show earlier tabs')}
          onClick={() => scrollByPage(-1)}
        >
          <ChevronLeft size={16} aria-hidden />
        </button>
      )}

      <div ref={stripRef} className={className} role="tablist" aria-label={ariaLabel}>
        {children}
      </div>

      {edges.right && (
        <button
          type="button"
          className="tab-scroller-arrow tab-scroller-arrow--right"
          aria-label={t('common:scrollTabsRight', 'Show more tabs')}
          onClick={() => scrollByPage(1)}
        >
          <ChevronRight size={16} aria-hidden />
        </button>
      )}
    </div>
  );
}

// ─── Summary lines ────────────────────────────────────────────────────────────

/**
 * The record's headline counts, written out as lines in the profile rather
 * than as tiles. Each line opens its tab. Lines the record has nothing for are
 * left out rather than shown as zero; while the record loads, a spinner holds
 * the place.
 */
export function MemberRecordSummary({ record, loading = false, onSelect }) {
  const { t } = useTranslation('common');

  if (loading && !record) {
    return (
      <div className="record-lines record-lines--loading" aria-busy="true">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  if (!record) return null;

  const legislation = record.legislation || {};
  const votes = record.votes || {};
  const agreement = votes.party_agreement || {};
  const totals = record.campaign_money?.totals || {};
  const trades = record.stock_trades || {};

  const lines = [
    isNum(legislation.sponsored) && {
      tab: 'bills',
      text: [
        t('common:lineSponsored', '{{count}} bills sponsored', { count: formatCount(legislation.sponsored) }),
        isNum(legislation.sponsored_became_law) &&
          t('common:lineBecameLaw', '{{count}} became law', { count: formatCount(legislation.sponsored_became_law) }),
      ],
    },
    isNum(legislation.cosponsored) && {
      tab: 'cosponsored',
      text: [
        t('common:lineCosponsored', '{{count}} bills cosponsored', { count: formatCount(legislation.cosponsored) }),
        isNum(legislation.cosponsored_became_law) &&
          t('common:lineBecameLaw', '{{count}} became law', { count: formatCount(legislation.cosponsored_became_law) }),
      ],
    },
    isNum(votes.roll_calls) && {
      tab: 'votes',
      text: [
        t('common:lineRollCalls', '{{count}} roll calls', { count: formatCount(votes.roll_calls) }),
        isNum(votes.missed_share) &&
          t('common:lineMissed', 'missed {{share}}', { share: formatPercent(votes.missed_share) }),
      ],
    },
    isNum(agreement.share_with_party) && {
      tab: 'votes',
      text: [
        t('common:lineWithParty', 'Voted with their party\u2019s majority on {{share}} of {{count}} compared', {
          share: formatPercent(agreement.share_with_party),
          count: formatCount(agreement.compared),
        }),
      ],
    },
    isNum(totals.raised) && {
      tab: 'money',
      text: [
        t('common:lineRaised', 'Campaign raised {{amount}}', { amount: formatDollars(totals.raised) }),
        isNum(totals.cash_on_hand) &&
          t('common:lineOnHand', '{{amount}} on hand', { amount: formatDollars(totals.cash_on_hand) }),
      ],
    },
    isNum(trades.transactions) && trades.transactions > 0 && {
      tab: 'trades',
      text: [t('common:lineTrades', '{{count}} stock trades reported', { count: formatCount(trades.transactions) })],
    },
  ].filter(Boolean);

  if (!lines.length) return null;

  return (
    <div className="record-lines">
      {lines.map((line, index) => (
        <button key={index} type="button" className="record-line-link" onClick={() => onSelect(line.tab)}>
          {line.text.filter(Boolean).join(', ')}
        </button>
      ))}
    </div>
  );
}

// ─── Votes ────────────────────────────────────────────────────────────────────

const VoteItem = ({ vote }) => {
  const { t } = useTranslation('common');
  const link = firstOf(vote, ['bill_url', 'legislation_url', 'congress_url', 'source_url']);

  const partyLine =
    vote.with_party_majority === false
      ? t('common:voteAgainstParty', 'Voted against most of their party, which voted {{position}}', {
          position: positionLabel(vote.party_majority, t).toLowerCase(),
        })
      : vote.with_party_majority === true
        ? t('common:voteWithParty', 'Voted with most of their party')
        : null;

  const where = [
    vote.chamber === 'senate' ? t('common:senate', 'Senate') : vote.chamber === 'house' ? t('common:house', 'House') : null,
    isNum(vote.number) ? t('common:rollCallNumber', 'roll call {{number}}', { number: vote.number }) : null,
    formatDay(vote.vote_date),
  ]
    .filter(Boolean)
    .join(', ');

  const details = [
    <span className="record-line">
      <span className="record-position">{vote.member_vote || positionLabel(vote.position, t)}</span>
      {vote.kind && <span className="trade-tag">{voteKindLabel(vote.kind, t)}</span>}
      {vote.their_own_bill && <span className="trade-tag">{t('common:theirBill', 'Their bill')}</span>}
    </span>,
    vote.description ? <span>{vote.description}</span> : null,
    vote.result ? <span className="record-result">{vote.result}</span> : null,
    partyLine ? <span className={vote.with_party_majority === false ? 'record-emphasis' : 'award-meta'}>{partyLine}</span> : null,
    where ? <span className="award-meta">{where}</span> : null,
  ];

  // House roll calls open the vote page (every member's vote on it, from
  // all_member_votes); the chamber's record or the bill sits beside More.
  // Senate roll calls have no page there, so their first button is Source.
  const moreUrl = voteDetailPath(vote);
  const billLink = firstOf(vote, ['bill_url', 'legislation_url', 'congress_url']);

  return (
    <FeedItem
      item={{
        id: vote.vote_id,
        title: vote.question,
        created_at: vote.vote_date,
        url: link,
        document_link: moreUrl ? link : null,
      }}
      feedName="Votes"
      branch="Legislative"
      topic="Member Votes"
      buttonName={billLink ? t('common:bill', 'Bill') : t('common:rollCall', 'Roll call')}
      schema=""
      table=""
      variant="default"
      details={details}
      shareUrl={shareLinkFor(moreUrl, link)}
      showDate={false}
      moreUrl={moreUrl}
    />
  );
};

/**
 * Roll calls from api2. Short (the tab) or full (the View More modal), where
 * it adds search, an against-the-party filter, the record's summary and
 * paging. `fallback` renders instead if api2 fails -- api1's list for House
 * members.
 */
export function MemberVotesList({ bioguideId, limit = 6, full = false, fallback = null }) {
  const { t } = useTranslation('common');
  const [query, setQuery] = useState('');
  const [againstParty, setAgainstParty] = useState(false);
  const q = useDebounced(query.trim());
  const pageSize = full ? 50 : limit;

  const list = usePagedList(
    (offset, size) => fetchMemberVotes(bioguideId, { q: q || undefined, againstParty, limit: size, offset }),
    [bioguideId, q, againstParty, pageSize],
    pageSize
  );

  if (list.error && fallback) return fallback;

  const summary = list.body?.summary;
  const agreement = summary?.party_agreement;

  return (
    <div className="record-list">
      {full && (
        <div className="record-controls">
          <SearchBox
            value={query}
            onChange={setQuery}
            placeholder={t('common:searchVotes', 'Search questions, bills and descriptions')}
          />
          <FeedSegments
            label={t('common:voteFilter', 'Which votes')}
            value={againstParty ? 'against' : 'all'}
            onChange={(id) => setAgainstParty(id === 'against')}
            options={[
              { id: 'all', label: t('common:allVotes', 'All votes') },
              { id: 'against', label: t('common:againstParty', 'Against their party') },
            ]}
          />
          {summary && (
            <p className="spending-hint record-summary">
              {[
                isNum(summary.roll_calls) &&
                  t('common:rollCallsCount', '{{count}} roll calls', { count: formatCount(summary.roll_calls) }),
                isNum(summary.missed) &&
                  t('common:missedCount', 'recorded as not voting in {{count}} ({{share}})', {
                    count: formatCount(summary.missed),
                    share: formatPercent(summary.missed_share),
                  }),
                agreement &&
                  isNum(agreement.share_with_party) &&
                  t('common:withPartyShare', 'with their party\u2019s majority on {{share}} of {{count}} compared', {
                    share: formatPercent(agreement.share_with_party),
                    count: formatCount(agreement.compared),
                  }),
              ]
                .filter(Boolean)
                .join('; ')}
              .{' '}
              {t(
                'common:voteRecordNote',
                'A missed vote is a fact about the record, not a reason: illness, travel and campaigning look the same in it.'
              )}
            </p>
          )}
        </div>
      )}

      <PanelState
        loading={list.loading}
        error={list.error}
        empty={!list.loading && !list.error && !list.rows.length}
        emptyText={t('common:noVotesFound', 'No roll calls match.')}
      />

      {!list.loading && list.rows.map((vote) => <VoteItem key={vote.vote_id} vote={vote} />)}

      {full && !list.loading && list.hasMore && (
        <MoreButton
          onClick={list.loadMore}
          loading={list.more}
          label={t('common:loadMoreCount', 'Load more ({{shown}} of {{total}})', {
            shown: list.rows.length,
            total: formatCount(list.total),
          })}
        />
      )}
    </div>
  );
}

// ─── Bills ────────────────────────────────────────────────────────────────────

const BillItem = ({ bill, role }) => {
  const { t } = useTranslation('common');

  const introduced = [
    bill.introduced_date &&
      t('common:introducedOn', 'Introduced {{date}}', { date: formatDay(bill.introduced_date) }),
    isNum(bill.cosponsor_count) &&
      t('common:cosponsorCount', '{{count}} cosponsors', { count: bill.cosponsor_count }),
  ]
    .filter(Boolean)
    .join(', ');

  const cosponsorLine =
    role === 'cosponsor'
      ? [
          bill.original && t('common:originalCosponsor', 'Original cosponsor'),
          !bill.original &&
            bill.sponsorship_date &&
            t('common:cosponsoredOn', 'Cosponsored {{date}}', { date: formatDay(bill.sponsorship_date) }),
          bill.withdrawn_date &&
            t('common:withdrawnOn', 'withdrew {{date}}', { date: formatDay(bill.withdrawn_date) }),
        ]
          .filter(Boolean)
          .join(', ')
      : null;

  const details = [
    <span className="record-line">
      {bill.stage && <span className="trade-tag">{billStageLabel(bill.stage, t)}</span>}
      {bill.law_number && (
        <span className="record-emphasis">
          {t('common:publicLaw', 'Public Law {{number}}', { number: bill.law_number })}
        </span>
      )}
      {bill.policy_area && <span className="award-meta">{bill.policy_area}</span>}
    </span>,
    cosponsorLine ? <span className="award-meta">{cosponsorLine}</span> : null,
    bill.latest_action_text ? (
      <span>
        {bill.latest_action_date ? `${formatDay(bill.latest_action_date)}: ` : ''}
        {bill.latest_action_text}
      </span>
    ) : null,
    introduced ? <span className="award-meta">{introduced}</span> : null,
  ];

  // More opens the existing bill page (its text, actions, committees and
  // sponsors) by GovInfo package id; Congress.gov sits beside it.
  return (
    <FeedItem
      item={{
        id: bill.bill_key,
        title: [bill.number, bill.title].filter(Boolean).join(': '),
        created_at: bill.introduced_date,
        url: bill.legislation_url || bill.source_url,
        document_link: bill.legislation_url || null,
      }}
      feedName="Bill Updates"
      branch="Legislative"
      topic={role === 'cosponsor' ? 'Cosponsored Bills' : 'Sponsored Bills'}
      buttonName="Congress.gov"
      schema=""
      table=""
      variant="default"
      details={details}
      shareUrl={shareLinkFor(billDetailPath(bill.bill_key), bill.legislation_url || bill.source_url)}
      showDate={false}
      moreUrl={billDetailPath(bill.bill_key)}
    />
  );
};

/**
 * Bills from api2, sponsored or cosponsored (separate lists in the API, and
 * separate tabs on the page). Short (the tab) or full (the modal, which can
 * switch between the two, with search and paging). `fallback` renders instead
 * if api2 fails.
 */
export function MemberBillsList({ bioguideId, limit = 6, full = false, fallback = null, role: initialRole = 'sponsor' }) {
  const { t } = useTranslation('common');
  const [role, setRole] = useState(initialRole);

  // The tab decides the list; a new tab starts the modal on the same role.
  useEffect(() => {
    setRole(initialRole);
  }, [initialRole]);
  const [query, setQuery] = useState('');
  const q = useDebounced(query.trim());
  const pageSize = full ? 50 : limit;

  const list = usePagedList(
    (offset, size) => fetchMemberBills(bioguideId, { role, q: q || undefined, limit: size, offset }),
    [bioguideId, role, q, pageSize],
    pageSize
  );

  if (list.error && fallback) return fallback;

  return (
    <div className="record-list">
      {full && (
        <div className="record-controls">
          <FeedSegments
            label={t('common:billRole', 'Role')}
            value={role}
            onChange={setRole}
            options={[
              { id: 'sponsor', label: t('common:sponsored', 'Sponsored') },
              { id: 'cosponsor', label: t('common:cosponsored', 'Cosponsored') },
            ]}
          />
          <SearchBox
            value={query}
            onChange={setQuery}
            placeholder={t('common:searchBillTitles', 'Search bill titles')}
          />
          {!list.loading && isNum(list.total) && (
            <p className="spending-hint record-summary">
              {t('common:billsThisCongress', '{{count}} in the current Congress, from GovInfo.', {
                count: formatCount(list.total),
              })}
            </p>
          )}
        </div>
      )}

      <PanelState
        loading={list.loading}
        error={list.error}
        empty={!list.loading && !list.error && !list.rows.length}
        emptyText={t('common:noBillsFound', 'No bills match.')}
      />

      {!list.loading && list.rows.map((bill) => <BillItem key={bill.bill_key} bill={bill} role={role} />)}

      {full && !list.loading && list.hasMore && (
        <MoreButton
          onClick={list.loadMore}
          loading={list.more}
          label={t('common:loadMoreCount', 'Load more ({{shown}} of {{total}})', {
            shown: list.rows.length,
            total: formatCount(list.total),
          })}
        />
      )}
    </div>
  );
}

// ─── Money ────────────────────────────────────────────────────────────────────

const Figure = ({ value, label }) =>
  value && value !== '\u2014' ? (
    <div className="party-stat record-stat">
      <div className="party-stat-value">{value}</div>
      <div className="party-stat-label">{label}</div>
    </div>
  ) : null;

const DonorList = ({ rows, name, sub, amount }) => (
  <ul className="record-rows">
    {rows.map((row, index) => (
      <li key={`${name(row)}-${index}`} className="record-row">
        <div className="record-row-main">
          {row.document_url ? (
            <span
              className="trade-link record-row-name"
              role="link"
              tabIndex={0}
              onClick={() => openExternal(row.document_url)}
              onKeyDown={onActivate(() => openExternal(row.document_url))}
            >
              {name(row)}
            </span>
          ) : (
            <span className="record-row-name">{name(row)}</span>
          )}
          {sub && sub(row) && <span className="record-row-sub">{sub(row)}</span>}
        </div>
        <span className="record-row-value">{amount(row)}</span>
      </li>
    ))}
  </ul>
);

export function MemberMoneyPanel({ record }) {
  const { t } = useTranslation('common');
  const money = record?.campaign_money || null;
  const pacs = Array.isArray(record?.leadership_pacs) ? record.leadership_pacs : [];

  if (!money && !pacs.length) {
    return (
      <FeedCard>
        <p className="party-lead">
          {t('common:noCampaignMoney', 'No FEC campaign record is linked to this member.')}
        </p>
      </FeedCard>
    );
  }

  const totals = money?.totals || {};
  const sources = Array.isArray(money?.where_it_came_from) ? money.where_it_came_from : [];
  const committeeDonors = money?.largest_committee_donors?.donors || [];
  const individualDonors = money?.largest_individual_donors?.donors || [];
  const supporting = money?.outside_spending?.supporting;
  const opposing = money?.outside_spending?.opposing;
  const sizes = money?.donation_sizes;
  const states = money?.donor_states;
  const others = Array.isArray(money?.other_candidacies) ? money.other_candidacies : [];
  const fecPage = money?.dive_deeper?.fec_candidate_page;

  return (
    <div className="record-panel">
      {money && (
        <FeedSection
          title={t('common:campaignMoney', 'Campaign money')}
          subtitle={t(
            'common:campaignMoneyNote',
            'The campaign\u2019s own committees, from the FEC. Leadership PACs, company PACs and outside spending are separate records and are never added in.'
          )}
        >
          <FeedCard
            footer={
              fecPage ? (
                <FeedButton primary onClick={() => openExternal(fecPage)}>
                  <BoxArrowUpRight size={12} className="me-1" aria-hidden />
                  {t('common:fecRecord', 'FEC record')}
                </FeedButton>
              ) : null
            }
          >
            {money.headline && <p className="party-lead">{money.headline.replace(/\.?$/, '.')}</p>}
            <div className="party-stats">
              <Figure value={formatDollars(totals.raised)} label={t('common:raised', 'raised')} />
              <Figure value={formatDollars(totals.spent)} label={t('common:spent', 'spent')} />
              <Figure value={formatDollars(totals.cash_on_hand)} label={t('common:cashOnHand', 'cash on hand')} />
            </div>
            {money.link?.matched_on && (
              <p className="spending-hint">
                {t('common:fecLinkNote', 'Linked to FEC candidate {{id}} by {{basis}}.', {
                  id: money.link.cand_id || '',
                  basis: money.link.matched_on,
                })}
              </p>
            )}
            {others.map((other) => (
              <p key={other.cand_id} className="spending-hint record-callout">
                {[other.headline, other.note].filter(Boolean).join(' ')}
              </p>
            ))}
          </FeedCard>

          {sources.length > 0 && (
            <FeedCard title={t('common:whereItCameFromShort', 'Where it came from')}>
              <Bars
                rows={sources}
                tone="gov"
                name={(row) => row.label}
                format={(value) => formatDollars(value)}
                sub={(row) => formatPercent(row.share)}
              />
            </FeedCard>
          )}

          {committeeDonors.length > 0 && (
            <FeedCard title={t('common:largestPacDonors', 'Largest PAC and committee donors')}>
              <DonorList
                rows={committeeDonors}
                name={(row) => titleCaseName(row.name)}
                sub={(row) =>
                  [row.kind, row.connected_organization && titleCaseName(row.connected_organization)]
                    .filter(Boolean)
                    .join(', ')
                }
                amount={(row) => formatDollars(row.total)}
              />
            </FeedCard>
          )}

          {individualDonors.length > 0 && (
            <FeedCard title={t('common:largestIndividualDonors', 'Largest individual donors')}>
              <DonorList
                rows={individualDonors}
                name={(row) => titleCaseName(row.name)}
                sub={(row) =>
                  [
                    [row.city && titleCaseName(row.city), row.state].filter(Boolean).join(', '),
                    [row.occupation, row.employer].filter(Boolean).map(titleCaseName).join(', '),
                  ]
                    .filter(Boolean)
                    .join(' \u00b7 ')
                }
                amount={(row) => formatDollars(row.total_in_gifts_read ?? row.total)}
              />
              <p className="spending-hint record-footnote">
                {t(
                  'common:individualDonorsNote',
                  'Ranked by the gifts actually read, not by the campaign\u2019s own running totals. Each name opens the filing that records it.'
                )}
              </p>
            </FeedCard>
          )}

          {(supporting || opposing) && (
            <FeedCard title={t('common:outsideSpending', 'Outside spending')}>
              <p className="spending-hint">
                {t(
                  'common:outsideSpendingNote',
                  'Spent by others, independently of the campaign. Supporting and opposing are separate, and neither is the campaign\u2019s money.'
                )}
              </p>
              {[
                ['supporting', supporting, t('common:supporting', 'Supporting')],
                ['opposing', opposing, t('common:opposing', 'Opposing')],
              ].map(([key, side, label]) =>
                side ? (
                  <div key={key} className="spending-list">
                    <h3 className="spending-list-title">
                      {label}: {formatDollars(side.total)}
                      {isNum(side.spenders) &&
                        ` \u00b7 ${t('common:spendersCount', '{{count}} spenders', { count: side.spenders })}`}
                    </h3>
                    {Array.isArray(side.largest_spenders) && (
                      <DonorList
                        rows={side.largest_spenders}
                        name={(row) => titleCaseName(row.name)}
                        sub={(row) => row.kind}
                        amount={(row) => formatDollars(row.total)}
                      />
                    )}
                  </div>
                ) : null
              )}
            </FeedCard>
          )}

          {(sizes || states) && (
            <FeedCard title={t('common:whoGave', 'Who gave')}>
              {sizes && Array.isArray(sizes.buckets) && (
                <div className="spending-list">
                  <h3 className="spending-list-title">
                    {isNum(sizes.small_dollar_share)
                      ? t('common:smallDollarShare', 'Gifts by size: {{share}} under $200', {
                          share: formatPercent(sizes.small_dollar_share),
                        })
                      : t('common:giftsBySize', 'Gifts by size')}
                  </h3>
                  <Bars
                    rows={sizes.buckets}
                    tone="kind"
                    name={(row) => row.label}
                    value={(row) => row.total}
                    format={(value) => formatDollars(value)}
                    sub={(row) => formatPercent(row.share)}
                  />
                  {sizes.note && <p className="spending-hint record-footnote">{sizes.note}</p>}
                </div>
              )}
              {states && Array.isArray(states.states) && (
                <div className="spending-list">
                  <h3 className="spending-list-title">
                    {isNum(states.home_state_share)
                      ? t('common:homeStateShare', 'Where itemized gifts came from: {{share}} from home', {
                          share: formatPercent(states.home_state_share),
                        })
                      : t('common:donorStates', 'Where itemized gifts came from')}
                  </h3>
                  <Bars
                    rows={states.states.slice(0, 8)}
                    tone="kind"
                    name={(row) => row.state}
                    value={(row) => row.total}
                    format={(value) => formatDollars(value)}
                    sub={(row) => formatPercent(row.share)}
                  />
                </div>
              )}
            </FeedCard>
          )}
        </FeedSection>
      )}

      {pacs.length > 0 && (
        <FeedSection
          title={t('common:leadershipPacs', 'Leadership PACs')}
          subtitle={t(
            'common:leadershipPacsNote',
            'Committees the member sponsors apart from their campaign. Their money is never part of what the campaign raised.'
          )}
        >
          {pacs.map((pac, index) => {
            const gave = Array.isArray(pac.gave_to_candidates) ? pac.gave_to_candidates : [];
            return (
              <FeedCard key={pac.committee_id || index} title={titleCaseName(firstOf(pac, ['name', 'committee', 'committee_name']) || '')}>
                <div className="party-stats">
                  <Figure value={formatDollars(pac.raised)} label={t('common:raised', 'raised')} />
                  <Figure value={formatDollars(pac.spent)} label={t('common:spent', 'spent')} />
                  <Figure value={formatDollars(pac.cash_on_hand)} label={t('common:cashOnHand', 'cash on hand')} />
                </div>
                {(isNum(pac.from_individuals) || isNum(pac.from_committees)) && (
                  <p className="spending-hint">
                    {[
                      isNum(pac.from_individuals) &&
                        t('common:fromIndividuals', '{{amount}} from individuals', { amount: formatDollars(pac.from_individuals) }),
                      isNum(pac.from_committees) &&
                        t('common:fromCommittees', '{{amount}} from other committees', { amount: formatDollars(pac.from_committees) }),
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                )}
                {gave.length > 0 && (
                  <div className="spending-list">
                    <h3 className="spending-list-title">{t('common:gaveToCandidates', 'Gave to candidates')}</h3>
                    <DonorList
                      rows={gave.slice(0, 10)}
                      name={(row) => titleCaseName(firstOf(row, ['name', 'candidate', 'candidate_name']) || '')}
                      sub={(row) => [row.party, row.office, row.state].filter(Boolean).join(', ')}
                      amount={(row) => formatDollars(firstOf(row, ['amount', 'total']))}
                    />
                  </div>
                )}
              </FeedCard>
            );
          })}
        </FeedSection>
      )}
    </div>
  );
}

// ─── Trades ───────────────────────────────────────────────────────────────────

/**
 * STOCK Act trades, in the same two sizes as the votes and bills lists:
 *
 *   tab    (limit)  the newest `limit` trades from the record, which the page
 *                   already holds, so the tab costs no request. The page puts
 *                   its View More button under it.
 *   full   (full)   every trade on file, fetched when the View More modal
 *                   opens, newest first.
 *
 * With neither, it renders as it did before either existed: the record's
 * newest trades and a Show all button that expands in place.
 *
 * The legal note on commercial use goes wherever the trades are shown.
 */
export function MemberTradesPanel({ bioguideId, record, limit = null, full = false }) {
  const { t } = useTranslation('common');
  const [expanded, setExpanded] = useState(false);
  const summary = record?.stock_trades || {};
  const recent = findRows(summary, ['recent', 'results', 'transactions_recent', 'newest'], (row) => 'tx_date' in row);

  // The whole list: straight away in the modal, on Show all otherwise.
  const showAll = full || expanded;
  const all = useApi2Resource(showAll ? () => fetchMemberTrades(bioguideId, { limit: 1000 }) : null, [
    bioguideId,
    showAll,
  ]);

  let rows = showAll && Array.isArray(all.data?.results) ? all.data.results : recent;
  if (!showAll && isNum(limit)) rows = rows.slice(0, limit);

  if (!isNum(summary.transactions) && !recent.length && !(showAll && all.data)) {
    return (
      <FeedCard>
        <p className="party-lead">
          {t('common:noTradesReported', 'No stock trades on file for this member.')}
        </p>
      </FeedCard>
    );
  }

  // The in-place Show all belongs to the original layout only: the tab has
  // View More under it, and the modal already holds everything.
  const offerShowAll =
    !full && !isNum(limit) && !expanded && isNum(summary.transactions) && summary.transactions > rows.length;

  const title = showAll
    ? isNum(summary.transactions)
      ? t('common:allTradesCount', 'All {{count}} trades', { count: formatCount(summary.transactions) })
      : t('common:allTrades', 'All trades')
    : t('common:newestTrades', 'Newest trades');

  return (
    <div className="record-panel">
      <FeedSection
        title={title}
        aside={
          offerShowAll ? (
            <div className="feed-item">
              <FeedButton primary onClick={() => setExpanded(true)}>
                {t('common:showAllCount', 'Show all {{count}}', { count: formatCount(summary.transactions) })}
              </FeedButton>
            </div>
          ) : null
        }
      >
        {showAll && <PanelState loading={all.loading} error={all.error} />}
        {!(showAll && all.loading) &&
          rows.map((trade, index) => (
            <TradeRow key={`${trade.url}-${trade.ticker}-${index}`} trade={trade} showMember={false} />
          ))}
        <div className="spending-notes">
          <p>
            {t(
              'common:tradesLawNote',
              'Federal law prohibits using these reports for commercial purposes other than news and communications media, to determine credit ratings, or to solicit money.'
            )}
          </p>
        </div>
      </FeedSection>
    </div>
  );
}

// ─── Companies ────────────────────────────────────────────────────────────────

const CompanyRow = ({ company }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const orgId = company.org_id || orgIdFromUrl(company.company_url);
  const kinds = Array.isArray(company.record_kinds)
    ? company.record_kinds
    : Array.isArray(company.records)
      ? [...new Set(company.records.map((r) => r.record))]
      : [];

  const open = () => orgId && navigate(companyPath(orgId));

  return (
    <li className="record-row record-row--stacked">
      <span
        className={`record-row-name ${orgId ? 'trade-link' : ''}`}
        role={orgId ? 'link' : undefined}
        tabIndex={orgId ? 0 : undefined}
        onClick={open}
        onKeyDown={orgId ? onActivate(open) : undefined}
      >
        {titleCaseName(company.name)}
      </span>
      {kinds.length > 0 && (
        <ul className="record-ties">
          {kinds.map((kind) => (
            <li key={kind}>{companyRecordLabel(kind, t)}</li>
          ))}
        </ul>
      )}
    </li>
  );
};

export function MemberCompaniesPanel({ bioguideId, record }) {
  const { t } = useTranslation('common');
  const [showAll, setShowAll] = useState(false);
  const linked = record?.linked_companies || {};
  const top = findRows(linked, ['results', 'companies', 'top'], (row) => 'name' in row);
  const total = isNum(linked.total) ? linked.total : top.length;
  const byRecord = linked.by_record && typeof linked.by_record === 'object' ? Object.entries(linked.by_record) : [];

  const all = useApi2Resource(showAll ? () => fetchMemberCompanies(bioguideId) : null, [bioguideId, showAll]);
  const rows = showAll && Array.isArray(all.data?.results) ? all.data.results : top;
  const otherPacs = showAll && Array.isArray(all.data?.other_connected_pacs) ? all.data.other_connected_pacs : [];
  const leadershipGave =
    showAll && Array.isArray(all.data?.leadership_pacs_that_gave) ? all.data.leadership_pacs_that_gave : [];

  if (!rows.length) {
    return (
      <FeedCard>
        <p className="party-lead">
          {t('common:noLinkedCompanies', 'No official record ties a company to this member.')}
        </p>
      </FeedCard>
    );
  }

  return (
    <div className="record-panel">
      <FeedCard
        footer={
          !showAll && total > top.length ? (
            <FeedButton primary onClick={() => setShowAll(true)}>
              {t('common:showAllCount', 'Show all {{count}}', { count: formatCount(total) })}
            </FeedButton>
          ) : null
        }
      >
        <p className="spending-hint">
          {t(
            'common:linkedCompaniesNote',
            'Each tie shows that an official record exists, not that anything was exchanged. Ordered by how many kinds of record tie the company, which is not a ranking of influence.'
          )}
        </p>

        {byRecord.length > 0 && (
          <div className="record-line record-chips">
            {byRecord
              .filter(([, count]) => count > 0)
              .map(([kind, count]) => (
                <span key={kind} className="trade-tag">
                  {companyRecordLabel(kind, t)}: {count}
                </span>
              ))}
          </div>
        )}

        {showAll && <PanelState loading={all.loading} error={all.error} />}
        {!(showAll && all.loading) && (
          <ul className="record-rows">
            {rows.map((company, index) => (
              <CompanyRow key={company.org_id || `${company.name}-${index}`} company={company} />
            ))}
          </ul>
        )}
      </FeedCard>

      {otherPacs.length > 0 && (
        <FeedCard title={t('common:otherConnectedPacs', 'Other PACs that gave')}>
          <p className="spending-hint">
            {t('common:otherConnectedPacsNote', 'PACs connected to unions, trade associations and membership groups.')}
          </p>
          <DonorList
            rows={otherPacs}
            name={(row) => titleCaseName(row.committee)}
            sub={(row) => [row.kind, row.connected_organization && titleCaseName(row.connected_organization)].filter(Boolean).join(', ')}
            amount={(row) => formatDollars(row.amount)}
          />
        </FeedCard>
      )}

      {leadershipGave.length > 0 && (
        <FeedCard title={t('common:leadershipPacsThatGave', 'Other politicians\u2019 leadership PACs that gave')}>
          <DonorList
            rows={leadershipGave}
            name={(row) => titleCaseName(row.committee)}
            amount={(row) => formatDollars(row.amount)}
          />
        </FeedCard>
      )}
    </div>
  );
}

// ─── Committees ───────────────────────────────────────────────────────────────

export function MemberCommitteesPanel({ record }) {
  const { t } = useTranslation('common');
  const committees = Array.isArray(record?.committees)
    ? record.committees
    : findRows(record?.committees, ['committees', 'results'], (row) => 'name' in row || 'committee' in row);

  if (!committees.length) {
    return (
      <FeedCard>
        <p className="party-lead">{t('common:noCommittees', 'No committee seats on file.')}</p>
      </FeedCard>
    );
  }

  return (
    <FeedCard>
      <ul className="record-rows">
        {committees.map((committee, index) => {
          const name = firstOf(committee, ['name', 'committee', 'committee_name']);
          const subs = Array.isArray(committee.subcommittees) ? committee.subcommittees : [];
          return (
            <li key={`${name}-${index}`} className="record-row record-row--stacked">
              <div className="record-row-main">
                <span className="record-row-name">{name}</span>
                {committee.position && <span className="trade-tag">{committee.position}</span>}
              </div>
              {subs.length > 0 && (
                <ul className="record-ties">
                  {subs.map((sub, subIndex) => {
                    const subName = typeof sub === 'string' ? sub : firstOf(sub, ['name', 'subcommittee', 'committee']);
                    const subPosition = typeof sub === 'object' ? sub.position : null;
                    return (
                      <li key={`${subName}-${subIndex}`}>
                        {subName}
                        {subPosition ? ` (${subPosition})` : ''}
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </FeedCard>
  );
}

// ─── Filings ──────────────────────────────────────────────────────────────────

export function MemberFilingsPanel({ record }) {
  const { t } = useTranslation('common');
  const disclosures = record?.financial_disclosures || {};
  const latest = disclosures.latest_annual_report || null;
  const reports = findRows(disclosures, ['reports', 'recent', 'results', 'filings'], (row) => 'url' in row || 'filed_date' in row);

  const describe = (report) =>
    [report.title || report.report_type, report.year && t('common:coversYear', 'covers {{year}}', { year: report.year })]
      .filter(Boolean)
      .join(', ');

  if (!latest && !reports.length) {
    return (
      <FeedCard>
        <p className="party-lead">{t('common:noFilings', 'No financial disclosure reports on file.')}</p>
      </FeedCard>
    );
  }

  return (
    <div className="record-panel">
      <p className="spending-hint record-panel-note">
        {t(
          'common:filingsNote',
          'Annual reports list assets, debts, income, outside positions and gifts in the value ranges the law sets. They are linked here, not read, and no net worth is claimed.'
        )}
      </p>

      {latest && (
        <FeedItem
          item={{ id: latest.url, title: describe(latest) || t('common:latestAnnualReport', 'Latest annual report'), created_at: latest.filed_date, url: latest.url }}
          feedName="Financial Disclosures"
          branch="Legislative"
          topic="Financial Disclosures"
          buttonName={null}
          schema=""
          table=""
          variant="default"
          shareUrl={latest.url}
          showDate={false}
          details={[
            <span className="trade-tag">{t('common:latestAnnualReport', 'Latest annual report')}</span>,
            latest.filed_date ? (
              <span className="award-meta">{t('common:filedOn', 'Filed {{date}}', { date: formatDay(latest.filed_date) })}</span>
            ) : null,
          ]}
        />
      )}

      {reports
        .filter((report) => !latest || report.url !== latest.url)
        .map((report, index) => (
          <FeedItem
            key={report.url || index}
            item={{ id: report.url || index, title: describe(report), created_at: report.filed_date, url: report.url }}
            feedName="Financial Disclosures"
            branch="Legislative"
            topic="Financial Disclosures"
            buttonName={null}
            schema=""
            table=""
            variant="default"
            shareUrl={report.url}
            showDate={false}
            details={[
              report.filed_date ? (
                <span className="award-meta">{t('common:filedOn', 'Filed {{date}}', { date: formatDay(report.filed_date) })}</span>
              ) : null,
            ]}
          />
        ))}
    </div>
  );
}

// Re-exported for the tab strip's labels.
export const RECORD_TABS = ['votes', 'bills', 'cosponsored', 'money', 'trades', 'companies', 'committees', 'filings'];

export const useRecordTabLabels = () => {
  const { t } = useTranslation('common');
  return useMemo(
    () => ({
      votes: t('common:votes', 'Votes'),
      bills: t('common:sponsored', 'Sponsored'),
      cosponsored: t('common:cosponsored', 'Cosponsored'),
      // Campaign committees and leadership PACs: all FEC campaign finance.
      money: t('common:campaignMoneyTab', 'Campaign $'),
      trades: t('common:stockTrades', 'Stock trades'),
      companies: t('common:companies', 'Companies'),
      committees: t('common:committees', 'Committees'),
      filings: t('common:disclosures', 'Disclosures'),
    }),
    [t]
  );
};